/**
 * React Query hooks for project dashboard data.
 */

import { useQuery } from '@tanstack/react-query';
import { format, addDays } from 'date-fns';
import { api } from '../lib/api';

export type ProjectStatus = 'on_track' | 'at_risk' | 'late';

/** Project from GET /api/v1/dashboards/projects */
export interface Project {
  id: string;
  gpSalesOrderNumber: string;
  customerName: string;
  earliestPromisedShipDate: string;
  latestPromisedShipDate: string;
  expectedShipDate: string | null;
  expectedShipDateWorst: string | null;
  status: ProjectStatus;
  daysVariance: number; // Positive = early, negative = late
  lineItemCount?: number;
  tasksTotal?: number;
  tasksComplete?: number;
}

export interface ProjectsSummary {
  total: number;
  onTrack: number;
  atRisk: number;
  late: number;
}

interface ProjectsResponse {
  data: {
    summary: ProjectsSummary;
    projects: Project[];
  };
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

interface UseProjectsOptions {
  limit?: number;
  offset?: number;
}

/**
 * Generate mock projects data for development.
 */
function generateMockProjects(): ProjectsResponse['data'] {
  const customers = ['Acme Corp', 'TechStart Inc', 'Global Systems', 'DataFlow Ltd', 'CloudNine Solutions'];
  const today = new Date();

  const projects: Project[] = Array.from({ length: 12 }, (_, i) => {
    const promisedDate = addDays(today, 10 + Math.floor(Math.random() * 30));
    const variance = Math.floor(Math.random() * 20) - 10; // -10 to +10 days
    const expectedDate = addDays(promisedDate, -variance);

    let status: ProjectStatus;
    if (variance >= 0) {
      status = 'on_track';
    } else if (variance >= -5) {
      status = 'at_risk';
    } else {
      status = 'late';
    }

    const tasksTotal = 10 + Math.floor(Math.random() * 40);
    const tasksComplete = Math.floor(Math.random() * tasksTotal);

    return {
      id: `project-${i + 1}`,
      gpSalesOrderNumber: `ORD${String(12345 + i).padStart(6, '0')}`,
      customerName: customers[i % customers.length] ?? 'Unknown',
      earliestPromisedShipDate: format(promisedDate, 'yyyy-MM-dd'),
      latestPromisedShipDate: format(addDays(promisedDate, Math.floor(Math.random() * 5)), 'yyyy-MM-dd'),
      expectedShipDate: format(expectedDate, 'yyyy-MM-dd'),
      expectedShipDateWorst: format(addDays(expectedDate, 3), 'yyyy-MM-dd'),
      status,
      daysVariance: variance,
      lineItemCount: 1 + Math.floor(Math.random() * 5),
      tasksTotal,
      tasksComplete,
    };
  });

  // Sort by variance (most late first)
  projects.sort((a, b) => a.daysVariance - b.daysVariance);

  const summary: ProjectsSummary = {
    total: projects.length,
    onTrack: projects.filter((p) => p.status === 'on_track').length,
    atRisk: projects.filter((p) => p.status === 'at_risk').length,
    late: projects.filter((p) => p.status === 'late').length,
  };

  return { summary, projects };
}

/**
 * Fetch projects dashboard data.
 */
export function useProjects(options: UseProjectsOptions = {}) {
  const { limit = 100, offset = 0 } = options;

  return useQuery({
    queryKey: ['projects-dashboard', { limit, offset }],
    queryFn: async () => {
      try {
        const response = await api.get<ProjectsResponse>('/dashboards/projects', {
          params: { limit, offset },
        });
        return response.data;
      } catch (error) {
        // In development, use mock data if API is unavailable
        if (import.meta.env.DEV) {
          console.warn('API unavailable, using mock projects data');
          return generateMockProjects();
        }
        throw error;
      }
    },
    staleTime: 30_000, // 30 seconds
    refetchInterval: 60_000, // Refetch every minute
    refetchOnWindowFocus: false, // Prevent duplicate calls on tab switch
  });
}

/**
 * Get only at-risk and late projects.
 */
export function useAtRiskProjects(options: UseProjectsOptions = {}) {
  const query = useProjects(options);

  const atRiskProjects = query.data?.projects.filter(
    (p) => p.status === 'at_risk' || p.status === 'late'
  ) ?? [];

  return {
    ...query,
    data: query.data ? {
      ...query.data,
      projects: atRiskProjects,
    } : undefined,
  };
}

/**
 * Get status color class for a project.
 */
export function getStatusColor(status: ProjectStatus): string {
  switch (status) {
    case 'on_track':
      return 'text-success';
    case 'at_risk':
      return 'text-warning';
    case 'late':
      return 'text-danger';
  }
}

/**
 * Get status badge styling.
 */
export function getStatusBadgeClass(status: ProjectStatus): string {
  switch (status) {
    case 'on_track':
      return 'bg-success-subtle text-success-foreground';
    case 'at_risk':
      return 'bg-warning-subtle text-warning-foreground';
    case 'late':
      return 'bg-danger-subtle text-danger-foreground';
  }
}

/**
 * Format variance as human-readable string.
 */
export function formatVariance(days: number): string {
  if (days === 0) return 'On schedule';
  if (days > 0) return `${days} day${days === 1 ? '' : 's'} early`;
  return `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} late`;
}
